import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { PageHeader } from "../../../CommonElements/PageTitle";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";
import TableC from "@/CommonElements/TableC";
import { useFlightsDetailsQuery, useDeleteFlightDetailMutation } from "@/services/reports";
import { useFilter } from "@/CommonElements/TableC/hooks/useFilter";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { DATE_TIME_FORMAT } from "@/utils/constants";
import { AirplaneLanding02Icon, AirplaneTakeOff02Icon } from "@hugeicons/core-free-icons/index";
import { DeleteIcon, Edit02Icon } from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { useFlightTerminalsQuery } from "@/services/basicData";
import BtnAsLink from "@/Component/BtnAsLink";
import { AddSquareIcon } from "@hugeicons/core-free-icons";
import { getFlightFormFields, toFilterFields } from "./flightFormConfig";

const Flights = () => {
  const { t } = useTranslation();

  return (
    <div>
      <PageHeader title={t("FLIGHTS")} sticky={false} />
      <Container fluid>
        <FlightsList />
      </Container>
    </div>
  );
};

export default Flights;



const FlightsList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const per_page = 10;
  const { searchValue, debouncedValue: search, setSearchValue } = useDebouncedSearch(
    "",
    500
  );
  const {
    filters,
    handleFilterApply,
    handleFilterReset, } = useFilter();
  const { t } = useTranslation();
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();
  const { confirm, ModalComponent } = useConfirm();

  // ==================
  // Side Effect
  // ==================
  const { data: terminalsRes } = useFlightTerminalsQuery();
  const terminals = terminalsRes?.data?.data || [];
  const { data: flightsRes, isLoading, isFetching } = useFlightsDetailsQuery({
    ...filters,
    search,
    page,
    per_page
  });
  const { mutate: deleteFlight, isPending: isDeleting } = useDeleteFlightDetailMutation();
  const flights = flightsRes?.data?.data || [];
  const meta = flightsRes?.data?.meta || {};


  const columns = [
    {
      name: t("TRAVEL_TYPE"),
      key: "direction",
      render: (value) =>
        <Flex align="center" gap={4}>
          {value?.code === "D" ? <HugeiconsIcon color="red" size={16} icon={AirplaneTakeOff02Icon} /> : <HugeiconsIcon color="green" size={16} icon={AirplaneLanding02Icon} />}
          {getLocalizedText(value?.ar, value?.en) || "-"}
        </Flex>
      ,
    },
    {
      name: t("FLIGHT_NUMBER"),
      key: "flight_no",
    },

    {
      name: t("FLIGHT_CODE"),
      key: "id",
    },
    {
      name: t("FLIGHT_DATE"),
      key: "flight_date",
      render: (value) => value ? getLocalizedDate(value, DATE_TIME_FORMAT) : "-",
    },
    {
      name: t("AIRLINE"),
      key: "airline",
      render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
    },
    {
      name: t("TERMINAL"),
      key: "terminal",
      render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
    },
    {
      name: t("ARRIVAL_CARD_AIRPORT"),
      key: "airport_code",
    },
    {
      name: t("DEPARTURE_CITY"),
      key: "from_city",
      render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
    },
    {
      name: t("ARRIVAL_CITY"),
      key: "to_city",
      render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
    },
    {
      name: t("SEATS_COUNT"),
      key: "max_capacity",
    },
    {
      name: t("ACTIONS"),
      key: "actions",
      filterable: false,
    },
  ];

  const handleDeleteClick = (id) => {
    confirm({
      title: t("CONFIRM"),
      content: t("CONFIRM_DELETE_FLIGHT_MESSAGE"),
      okText: t("CONFIRM"),
      cancelText: t("CANCEL"),
      onOk: ({ closeModal }) => {
        deleteFlight(id, {
          onSuccess: () => closeModal(),
        });
        return false;
      },
      onCancel: () => {},
    });
  };

  const getActions = (rowData) => [
    {
      key: "edit",
      label: (
        <div
          className="d-flex align-items-center gap-2"
          role="button"
          onClick={() => navigate(`edit/${rowData.id}`, { state: { row: rowData } })}
        >
          <HugeiconsIcon size={16} icon={Edit02Icon} />
          {t("EDIT")}
        </div>
      ),
    },
    { type: "divider" },
    {
      key: "delete",
      label: (
        <div className="d-flex align-items-center gap-2">
          <HugeiconsIcon size={16} icon={DeleteIcon} />
          {t("DELETE")}
        </div>
      ),
      danger: true,
      onClick: () => handleDeleteClick(rowData.id),
    },
  ];

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setPage(1);
  }

  const handlePaginationChange = (page) => {
    setPage(page);
  }

  const flightFormFields = getFlightFormFields(t, getLocalizedText, terminals);
  const filterFields = toFilterFields(flightFormFields, t, getLocalizedText);

  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link="create"
      disabled={isLoading || isFetching}
    >
      {t("CREATE_FLIGHT")}
    </BtnAsLink>
  );

  return (
    <>
      <ModalComponent loading={isDeleting} />
      <TableC
        data={flights}
        columns={columns}
        loading={isLoading || isFetching}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        pagination={{
          current: page,
          total: meta?.total ?? 0,
          pageSize: meta?.per_page ?? 10,
          onChange: handlePaginationChange,
        }}
        filterFields={filterFields}
        filters={filters}
        onFilterApply={handleFilterApply(() => setPage(1))}
        onFilterReset={handleFilterReset}
        renderActions={createButton}
        actions={getActions}
      />
    </>
  );
};


